import { useState, useCallback } from 'react'
import { useFileUpload } from './useFileUpload'
import { useCreateProductList } from './useProductLists'
import { useSSE, type SSEEvent, type ProgressSSEMessage } from './useSSE'
import { getErrorMessage } from '@/utils/getErrorMessage'

export type UploadStatus =
  | 'idle'
  | 'uploading'
  | 'creating'
  | 'processing'
  | 'completed'
  | 'error'

export interface HistoricalDataUploadState {
  status: UploadStatus
  progress: number
  fileName: string | null
  fileId: string | null
  productListId: string | null
  error: string | null
  eventsUrl: string | null
}

export interface HistoricalDataUploadResult {
  fileId: string
  productListId: string
  fileName: string
}

const initialState: HistoricalDataUploadState = {
  status: 'idle',
  progress: 0,
  fileName: null,
  fileId: null,
  productListId: null,
  error: null,
  eventsUrl: null,
}

/**
 * Orchestrates the complete historical data file upload flow:
 * 1. Upload file to blob storage
 * 2. Create product list from file
 * 3. Subscribe to SSE for processing status
 * 4. Return product list ID when complete
 */
export function useHistoricalDataUpload() {
  const [state, setState] = useState<HistoricalDataUploadState>(initialState)

  const fileUploadMutation = useFileUpload()
  const createProductListMutation = useCreateProductList()

  // Handle SSE messages
  const handleSSEMessage = useCallback((event: SSEEvent) => {
    const data = event.data as ProgressSSEMessage
    // Handle both backend format (percent) and legacy format (progress)
    const progress = data.percent ?? data.progress

    setState(prev => ({
      ...prev,
      progress: progress ?? prev.progress,
      status: 'processing',
    }))
  }, [])

  // Handle SSE completion
  const handleSSEComplete = useCallback((event: SSEEvent) => {
    const { type } = event
    const data = event.data as ProgressSSEMessage

    // Check for completion via event type OR status field
    if (type === 'complete' || data.status === 'COMPLETED') {
      setState(prev => ({
        ...prev,
        status: 'completed',
        progress: 100,
        eventsUrl: null,
      }))
    } else if (type === 'error' || data.status === 'FAILED') {
      setState(prev => ({
        ...prev,
        status: 'error',
        error: data.error ?? data.message ?? 'Processing failed',
        eventsUrl: null,
      }))
    }
  }, [])

  // Handle SSE errors
  const handleSSEError = useCallback((error: Error) => {
    setState(prev => ({
      ...prev,
      status: 'error',
      error: error.message,
      eventsUrl: null,
    }))
  }, [])

  // Subscribe to SSE when we have an events URL
  useSSE({
    url: state.eventsUrl,
    onMessage: handleSSEMessage,
    onComplete: handleSSEComplete,
    onError: handleSSEError,
    enabled: !!state.eventsUrl,
  })

  /**
   * Upload a file and create a product list
   * Returns the product list ID when processing starts
   */
  const upload = useCallback(
    async (file: File): Promise<HistoricalDataUploadResult> => {
      // Reset state
      setState({
        ...initialState,
        status: 'uploading',
        fileName: file.name,
      })

      try {
        // Step 1: Upload file
        const fileResult = await fileUploadMutation.mutateAsync(file)

        setState(prev => ({
          ...prev,
          status: 'creating',
          fileId: fileResult.fileId,
          progress: 33,
        }))

        // Step 2: Create product list
        const productListResult = await createProductListMutation.mutateAsync({
          fileId: fileResult.fileId,
          name: `Import from ${file.name}`,
        })

        // Step 3: Subscribe to SSE for processing status
        setState(prev => ({
          ...prev,
          status: 'processing',
          productListId: productListResult.resource_id,
          eventsUrl: productListResult.events_url,
          progress: 66,
        }))

        return {
          fileId: fileResult.fileId,
          productListId: productListResult.resource_id,
          fileName: file.name,
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error, 'Upload failed')
        setState(prev => ({
          ...prev,
          status: 'error',
          error: errorMessage,
        }))
        throw error
      }
    },
    [fileUploadMutation, createProductListMutation]
  )

  /**
   * Reset the upload state
   */
  const reset = useCallback(() => {
    setState(initialState)
    fileUploadMutation.reset()
    createProductListMutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    ...state,
    upload,
    reset,
    isUploading: state.status === 'uploading',
    isCreating: state.status === 'creating',
    isProcessing: state.status === 'processing',
    isCompleted: state.status === 'completed',
    isError: state.status === 'error',
    isPending:
      state.status === 'uploading' ||
      state.status === 'creating' ||
      state.status === 'processing',
  }
}

// Re-export for backwards compatibility
export { useHistoricalDataUpload as useUploadHistoricalData }
